import { useLocation, useNavigate } from '@tanstack/react-router'
import { LogOut, Monitor, Moon, PencilLine, Settings, Shield, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Locale } from '@hub/contracts'
import { useState, useRef } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { TeamAvatarStack } from '@/components/team-avatar-stack'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationBell } from '@/components/notification-bell'
import { Separator } from '@/components/ui/separator'
import { MobileNav } from '@/components/mobile-nav'
import { LocaleFlag, SwedishFlag, UsFlag } from '@/components/flag-icons'
import { useAuthState } from '@/lib/auth-state'
import { useOptionalProject } from '@/lib/project-context'
import { useSettingsDialog } from '@/components/settings/use-settings-dialog'
import { usePresence } from '@/lib/collab/presence-context'
import { getPageKey } from '@/lib/navigation'
import { useLocale } from '@/i18n/locale'
import { orpc } from '@/lib/orpc-client'
import { toApiUrl } from '@/lib/orpc-client'
import { CURRENT_VERSION } from '@/lib/app-version'
import { usePageSubtitle } from '@/lib/page-title-context'
import { useTheme, type ThemePreference } from '@/lib/theme'
import { cn } from '@/lib/utils'

export function Navbar() {
  const { me, logout, refresh } = useAuthState()
  const { t } = useTranslation()
  const { locale, setLocale } = useLocale()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const projectCtx = useOptionalProject()
  const settingsDialog = useSettingsDialog()
  const { connectionStatus } = usePresence()
  const { subtitle, onRename, actions } = usePageSubtitle()
  const {
    preference: themePreference,
    resolved: resolvedTheme,
    setPreference: setTheme,
  } = useTheme()
  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editValue, setEditValue] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)

  const pageKey = getPageKey(pathname)
  const pageName = pageKey ? t(`nav.${pageKey}`) : t('app.name')

  const changeLanguage = (next: Locale) => {
    if (next === locale) return
    setLocale(next)
    // Persist on the profile so the choice follows the user between devices.
    void orpc.me
      .updateProfile({ locale: next })
      .then(() => refresh())
      .catch(() => {})
  }

  const startEditing = () => {
    if (!subtitle || !onRename) return
    setEditValue(subtitle)
    setIsEditingTitle(true)
    setTimeout(() => editInputRef.current?.select(), 0)
  }

  const commitEdit = async () => {
    const trimmed = editValue.trim()
    if (!trimmed || !onRename) {
      setIsEditingTitle(false)
      return
    }
    try {
      await onRename(trimmed)
    } catch {
      // keep old title on error
    }
    setIsEditingTitle(false)
  }
  const ringColor =
    connectionStatus === 'connected'
      ? 'ring-green-500'
      : connectionStatus === 'connecting'
        ? 'ring-yellow-500'
        : 'ring-red-500'
  const userName = me?.user.name?.trim() || t('userMenu.anonymous')
  const avatarUrl = me?.user.avatarUrl ? toApiUrl(me.user.avatarUrl) : undefined
  const avatarFallback = userName.slice(0, 1).toUpperCase()

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logout()
    } finally {
      setIsLoggingOut(false)
    }
  }

  return (
    <header className="border-border/40 bg-background/55 absolute inset-x-0 top-0 z-30 flex h-(--topbar-h) flex-shrink-0 items-center justify-between border-b px-4 backdrop-blur-xl dark:border-white/12">
      <div className="flex items-center gap-1 min-w-0">
        <MobileNav className="-ms-2 me-1" />
        <span className="text-lg font-semibold text-foreground shrink-0">{pageName}</span>
        {subtitle && (
          <>
            <span className="text-lg text-muted-foreground shrink-0"> — </span>
            {isEditingTitle ? (
              <input
                ref={editInputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => commitEdit()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitEdit()
                  if (e.key === 'Escape') setIsEditingTitle(false)
                }}
                className="text-lg font-semibold text-foreground bg-transparent border-b border-primary outline-none min-w-0"
              />
            ) : (
              <button
                onClick={onRename ? startEditing : undefined}
                className={cn(
                  'flex items-center gap-1.5 text-lg font-semibold text-foreground truncate min-w-0 group/title',
                  onRename && 'hover:text-foreground cursor-text'
                )}
              >
                {subtitle}
                {onRename && (
                  <PencilLine className="h-3.5 w-3.5 opacity-0 group-hover/title:opacity-100 transition-opacity text-muted-foreground shrink-0" />
                )}
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        {actions && (
          <>
            {actions}
            <Separator orientation="vertical" className="mx-1 data-[orientation=vertical]:h-5" />
          </>
        )}
        <TeamAvatarStack />
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors outline-none">
              <Avatar
                className={cn('h-8 w-8 ring-2 ring-offset-1 ring-offset-background', ringColor)}
              >
                {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
                <AvatarFallback>{avatarFallback}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-foreground max-md:hidden phone-landscape:hidden">
                {userName}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() =>
                projectCtx ? settingsDialog.open('profile') : navigate({ to: '/settings' })
              }
            >
              <Settings className="h-4 w-4" />
              {t('userMenu.profileSettings')}
            </DropdownMenuItem>
            {me?.isSuperAdmin && (
              <DropdownMenuItem onClick={() => navigate({ to: '/admin' })}>
                <Shield className="h-4 w-4" />
                {t('userMenu.superAdmin')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ThemeIcon className="h-4 w-4" />
                {t('userMenu.theme')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={themePreference}
                  onValueChange={(value) => setTheme(value as ThemePreference)}
                >
                  <DropdownMenuRadioItem value="light">
                    <Sun className="h-4 w-4" />
                    {t('userMenu.light')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">
                    <Moon className="h-4 w-4" />
                    {t('userMenu.dark')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">
                    <Monitor className="h-4 w-4" />
                    {t('userMenu.system')}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <LocaleFlag locale={locale} className="h-4 w-4" />
                {t('userMenu.language')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={locale}
                  onValueChange={(value) => changeLanguage(value as Locale)}
                >
                  <DropdownMenuRadioItem value="sv">
                    <SwedishFlag className="h-4 w-4" />
                    {t('language.sv')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="en">
                    <UsFlag className="h-4 w-4" />
                    {t('language.en')}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} disabled={isLoggingOut}>
              <LogOut className="h-4 w-4" />
              {isLoggingOut ? t('userMenu.loggingOut') : t('userMenu.logout')}
            </DropdownMenuItem>
            <div className="px-2 py-1.5 text-center">
              <span className="text-[10px] text-muted-foreground">HUB {CURRENT_VERSION}</span>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
